import React, { useContext, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Typography } from '@material-ui/core';
import { saveAs } from 'file-saver';
import parser from 'content-disposition';
import { get } from 'lodash';

import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { Loader } from 'components/loader/loader';
import fetch from 'utils/fetch';

import { acknowledgeOrder } from './order-api';

export function OrderAcknowledgeDialog({
  orderId,
  handleClose,
}: {
  orderId?: string;
  handleClose: () => void;
}) {
  const { showNotification } = useContext(NotificationCtx);
  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    if (!orderId) {
      return;
    }

    const downloadLink = '/api/report/generate?templateType=ORDER_CZ&type=PDF';

    setLoading(true);
    try {
      const response = await fetch(downloadLink, {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          params: {
            filters: [
              {
                operation: 'EQ',
                field: 'id',
                value: orderId,
              },
            ],
          },
        }),
      });

      const file = await response.blob();
      const fileNameFromResponse = extractFileName(response);

      saveAs(file, fileNameFromResponse ?? 'order-confirmation.pdf');
      setSigned(true);
    } catch (e) {
      showNotification(
        'Při pokusu o stáhnutí potvrzení došlo k chybě.',
        NotificationVariant.ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    const { ok, data } = await acknowledgeOrder(orderId!);

    if (ok) {
      showNotification(
        'Žádost uložena ako podepsáná',
        NotificationVariant.SUCCESS
      );
      handleClose();
    } else {
      showNotification(
        `Při podepsání žádosti došlo k chybě
				${(data as { message: string })?.message}`,
        NotificationVariant.ERROR
      );
    }
  };

  return (
    <Dialog open={!!orderId} onClose={handleClose} scroll="paper">
      <DialogTitle>Potvrzení objednávky</DialogTitle>
      <DialogContent dividers={true}>
        {loading ? (
          <div
            style={{
              minHeight: 150,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div>
              <Typography style={{ marginBottom: 8 }}>
                Probíhá generovaní PDF...
              </Typography>
              <Loader />
            </div>
          </div>
        ) : (
          <DialogContentText>
            <p>Vytisknete potvrzení které badatel podepíše.</p>
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSign}
          color="primary"
          disabled={!signed || loading}
        >
          Podepsáno
        </Button>
        <Button onClick={handlePrint} disabled={loading}>
          Vytisknout
        </Button>
        <Button onClick={handleClose}>Zavřít</Button>
      </DialogActions>
    </Dialog>
  );
}

function extractFileName(response: Response) {
  const headers = response.headers;
  const contentDisposition = headers.get('content-disposition');

  if (contentDisposition) {
    const disposition = parser.parse(contentDisposition);
    return get(disposition, 'parameters.filename') as string;
  }

  return undefined;
}
